import { produce } from "immer";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { filterMaybeArray } from "@examspark/common";
import {
  questionTypeMeta,
  questionTypesList,
} from "@examspark/common/dist/meta/questions";
import {
  Button,
  CheckboxButton,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconRejectLine,
} from "@examspark/common-ui/dist/components/icons";
import { InteractionTypeEnum, BucketAggregation } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useExamBuilderSearch } from "../hooks/useSearch";

const ItemTypes = styled.div`
  display: grid;
  grid-gap: 0.5rem 0.5rem;
  grid-template-columns: repeat(auto-fill, minmax(19rem, 1fr));
`;

const Styled = styled.div`
  position: relative;
`;

const StyledCheckboxButton = styled(CheckboxButton)`
  background-color: #f2f2f2;

  &:hover {
    background-color: #e7e7e7;
  }
`;

const Count = styled.div`
  background-color: #006699;
  color: #fff;
  border-radius: 1rem;
  text-align: center;
  padding: 0.3rem 0.4rem;
  margin: -0.3rem 0;
  font-size: 1rem;
`;

const ItemTypeCount = styled.div`
  width: 4rem;
  text-align: right;
  color: #555;
  font-size: 1rem;
`;

const itemTypeLabelOverrides: {
  [key: string]: string;
} = {
  [InteractionTypeEnum.TextExtended]: "Essay",
  [InteractionTypeEnum.TextSelectable]: "HotText",
  [InteractionTypeEnum.TextGapMatch]: "Drag & Drop (Text)",
  [InteractionTypeEnum.GraphicHotspot]: "Hotspot",
  [InteractionTypeEnum.TextInlineChoice]: "Inline Choice",
  [InteractionTypeEnum.TextInlineEntry]: "Text Entry",
  [InteractionTypeEnum.TextInlineMathEntry]: "Equation Entry",
  [InteractionTypeEnum.TextInlineNumericEntry]: "Numeric Entry",
  [InteractionTypeEnum.Order]: "Sorting/Ordering",
  [InteractionTypeEnum.GraphicGapMatch]: "Drag & Drop (Graphic)",
  [InteractionTypeEnum.Audio]: "Audio Response",
};

const betaItemTypes: InteractionTypeEnum[] = [
  InteractionTypeEnum.Audio,
  InteractionTypeEnum.Gridded,
  // InteractionTypeEnum.BarChart,
  // InteractionTypeEnum.CartesianGraph,
  // InteractionTypeEnum.GraphicGapMatch,
  // InteractionTypeEnum.GraphicHotspot,
  // InteractionTypeEnum.LineChart,
  // InteractionTypeEnum.NumberLinePlot,
];

const NewActivityQuestionTypes: React.FC<React.PropsWithChildren<unknown>> = (
  props,
) => {
  const search = useExamBuilderSearch();
  const [state, dispatchState] = React.useContext(NewActivityContext);
  const [facets, setFacets] = useState<BucketAggregation[]>();

  const fetchResults = async () => {
    if (!state.sections) return;
    if (!state.objectives) return;
    if (!state.document) return;
    if (!state.language) return;

    const exclude: string[] = ["interactionTypes"];
    // if (!state.isIncludeOnlyPassageItemsEnabled) {
    //   exclude.push("passages");
    // }

    const results = await search.searchItems({
      exclude,
      label: "QuestionTypes",
    });

    const _facets = filterMaybeArray<BucketAggregation>(
      results?.aggregations?.interaction?.buckets ?? [],
    );

    setFacets(_facets);
  };

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.document,
    state.sections,
    state.language,
    state.passages,
    state.isIncludeOnlyPassageItemsEnabled,
    state.isIncludeAllPassageItemsEnabled,
  ]);

  const handleSelectAll = (selected?: boolean) => {
    if (!selected) {
      dispatchState({
        type: "UPDATE",
        data: produce(state, (draft) => {
          draft.interactionTypes = [];
        }),
      });
      return;
    }

    dispatchState({
      type: "UPDATE",
      data: produce(state, (draft) => {
        draft.interactionTypes = questionTypesList.filter(
          (q) => !betaItemTypes.includes(q),
        );
      }),
    });
  };

  return (
    <Styled>
      <Text.H2>Question Type Preferences</Text.H2>
      <VSpace size="1rem">
        <Text.Body>
          Select all the question types you’d like to include in your activity.
        </Text.Body>
        <VSpace>
          <Toolbar>
            <Button
              variant="outlined"
              title="Select All"
              icon={<IconAcceptLine />}
              onClick={() => handleSelectAll(true)}
            >
              All
            </Button>
            <Button
              variant="outlined"
              title="Select None"
              icon={<IconRejectLine />}
              onClick={() => handleSelectAll(false)}
            >
              None
            </Button>
          </Toolbar>
          <ItemTypes>
            {questionTypesList.map((q) => {
              if (betaItemTypes.includes(q)) return null;

              const meta = questionTypeMeta[q];
              const count =
                facets?.find?.((v: any) => {
                  return v.key === q;
                })?.doc_count || 0;

              return (
                <StyledCheckboxButton
                  key={q}
                  title={`${q}`}
                  disabled={!count}
                  checked={state.interactionTypes.includes(q)}
                  onClick={() => {
                    dispatchState({
                      type: "UPDATE",
                      data: produce(state, (draft) => {
                        const findIndex = draft.interactionTypes.findIndex(
                          (t) => t === q,
                        );
                        if (findIndex !== -1) {
                          draft.interactionTypes.splice(findIndex, 1);
                        } else {
                          draft.interactionTypes.push(q);
                        }
                      }),
                    });
                  }}
                >
                  <div style={{ flex: 1, textAlign: "left" }}>
                    {itemTypeLabelOverrides[q]
                      ? itemTypeLabelOverrides[q]
                      : meta.label}
                  </div>
                  {!!count && (
                    <ItemTypeCount>
                      <Count>{count}</Count>
                    </ItemTypeCount>
                  )}
                </StyledCheckboxButton>
              );
            })}
          </ItemTypes>
          {/* <div>
          <Toolbar>
            <Button title="Refresh" onClick={() => fetchResults()}>
              Refresh
            </Button>
          </Toolbar>
        </div> */}
        </VSpace>
      </VSpace>
    </Styled>
  );
};

export default NewActivityQuestionTypes;
